/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Blackbox } from '../../basic/Blackbox';
import Text from '../../basic/Text';
import { CardWithSections } from '../../basic/card/Card';
import CardHeader from '../../basic/card/CardHeader';
import Icon from '../../basic/Icon';
import { ReactNode, ComponentType } from "react";
import { cssKeyframes } from '@/shared/styles/animations';
import { CardSection } from '../../basic/card/CardSection';
import { FlexFill } from '../../basic/Flex';
import ActionIcon from '../../basic/ActionIcon';
import { Cross2Icon } from "@radix-ui/react-icons";

type PopupProps = {
  onClose: () => void;
  children: ReactNode;
  icon: ReactNode | ComponentType;
  title: string;
  headerContent?: ReactNode;
  popupSx?: React.CSSProperties;
};
const Popup: ComponentWithChildren<PopupProps> = ({
  onClose,
  children,
  icon,
  title,
  popupSx,
  headerContent,
  sx,
  ...props
}) => {
  return (
    <Blackbox
      onClick={(event: MouseEvent) => {
        event.currentTarget === event.target && onClose();
      }}
    >
      <CardWithSections
        size="small"
        sx={{
          display: "flex",
          flexFlow: "column",
          width: "min(480px, calc(100vw - 2rem))",
          background: "var(--surface-primary)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-lg)",
          animation: `${cssKeyframes.pop} 0.2s backwards`,
          cursor: "default",
          ...popupSx,
        }}
        {...props}
      >
        <CardHeader
          style={{
            gap: "var(--space-3)",
            alignItems: "center",
            padding: "var(--space-4)",
          }}
        >
          <Icon as={icon} size="xl" style={{ color: "var(--status-warning-text)" }} />
          <Text fw="var(--font-bold)" className="flex items-center text-(--text-primary)">
            {title}
          </Text>
          <FlexFill />
          {headerContent}
          <ActionIcon size="sm" variant="subtle" onClick={onClose}>
            <Icon as={Cross2Icon} />
          </ActionIcon>
        </CardHeader>
        <CardSection
          direction="vertical"
          style={{
            padding: "var(--space-4)",
            paddingTop: 0,
            gap: "var(--space-4)",
            ...sx,
          }}
        >
          {children}
        </CardSection>
      </CardWithSections>
    </Blackbox>
  );
};

export default Popup;
